import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ChronologyEmptyStateIllustration } from './chronology-empty-state-illustration';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface HowToCreateChronologyDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  subsetId: string;
}

export function HowToCreateChronologyDialog({
  isOpen,
  onOpenChange,
  matterId,
  subsetId,
}: HowToCreateChronologyDialogProps) {
  const { t } = useTranslation();
  const kimPath = `/matters/${matterId}/subsets/${subsetId}/new-kim?template=chronology`;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--git-fork" />
            How to Create a Chronology
          </DialogTitle>
          <DialogDescription aria-hi>
            Choose the workflow that best matches how you want to build your chronology.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-12 mb-8 flex w-full scale-110 justify-center">
          <ChronologyEmptyStateIllustration />
        </div>

        <p className="mb-6 px-3">
          Chronologies in Wexler allow you to construct timelines from a snapshot of your facts. These timelines can
          then be edited and exported.
        </p>

        <div className="space-y-3">
          <HowToCreateOption
            title="Ask Kim to draft one"
            description="Specify exactly what issues, entities, or time periods you're interested in, and refine it from there."
            to={kimPath}
            cta="Open Kim"
            onOpenChange={onOpenChange}
          />
          <HowToCreateOption
            title="From the facts table"
            description="Pick the exact facts you want, then add them to a new chronology."
            to={`/matters/${matterId}/subsets/${subsetId}/facts`}
            cta="Open the facts table"
            onOpenChange={onOpenChange}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HowToCreateOption({
  title,
  description,
  to,
  cta,
  onOpenChange,
}: {
  title: string;
  description: string;
  to: string;
  cta: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <Button asChild variant="white" size="sm" className="shrink-0">
        <Link to={to} onClick={() => onOpenChange(false)}>
          {cta}
          <Icon className="lucide--arrow-up-right" />
        </Link>
      </Button>
    </div>
  );
}
